import React, { useState } from "react";

const TimesheetIllustration = () => (
  <svg
    className="auth-illustration"
    viewBox="0 0 440 340"
    role="img"
    aria-hidden="true"
    focusable="false"
  >
    {/* soft brand backdrop */}
    <rect x="26" y="24" width="388" height="284" rx="26" fill="var(--primary-color)" opacity="0.1" />
    {/* card shadow + timesheet card */}
    <rect x="80" y="68" width="240" height="224" rx="14" fill="var(--auth-ink)" opacity="0.08" />
    <rect x="70" y="58" width="240" height="224" rx="14" fill="#fff" stroke="var(--auth-ink)" strokeOpacity="0.14" />
    {/* card header */}
    <circle cx="100" cy="92" r="12" fill="var(--primary-color)" opacity="0.4" />
    <rect x="122" y="82" width="92" height="8" rx="4" fill="var(--auth-ink)" opacity="0.75" />
    <rect x="122" y="96" width="56" height="6" rx="3" fill="var(--auth-ink)" opacity="0.25" />
    <rect x="88" y="118" width="204" height="1.5" rx="0.75" fill="var(--auth-ink)" opacity="0.1" />
    {/* day rows with hour bars */}
    <rect x="88" y="132" width="26" height="7" rx="3.5" fill="var(--auth-ink)" opacity="0.3" />
    <rect x="126" y="130" width="106" height="11" rx="5.5" fill="var(--primary-color)" opacity="0.4" />
    <rect x="88" y="158" width="26" height="7" rx="3.5" fill="var(--auth-ink)" opacity="0.3" />
    <rect x="126" y="156" width="150" height="11" rx="5.5" fill="var(--primary-color)" opacity="0.4" />
    <rect x="88" y="184" width="26" height="7" rx="3.5" fill="var(--auth-ink)" opacity="0.3" />
    <rect x="126" y="182" width="126" height="11" rx="5.5" fill="var(--primary-color)" />
    <rect x="88" y="210" width="26" height="7" rx="3.5" fill="var(--auth-ink)" opacity="0.3" />
    <rect x="126" y="208" width="74" height="11" rx="5.5" fill="var(--primary-color)" opacity="0.4" />
    {/* total row */}
    <rect x="88" y="244" width="48" height="8" rx="4" fill="var(--auth-ink)" opacity="0.5" />
    <rect x="232" y="238" width="60" height="20" rx="10" fill="var(--auth-ink)" />
    <rect x="245" y="245.5" width="34" height="5" rx="2.5" fill="#fff" opacity="0.9" />
    {/* clock */}
    <circle cx="330" cy="232" r="56" fill="#fff" stroke="var(--primary-color)" strokeWidth="10" />
    <circle cx="330" cy="188" r="2.5" fill="var(--auth-ink)" opacity="0.4" />
    <circle cx="374" cy="232" r="2.5" fill="var(--auth-ink)" opacity="0.4" />
    <circle cx="330" cy="276" r="2.5" fill="var(--auth-ink)" opacity="0.4" />
    <circle cx="286" cy="232" r="2.5" fill="var(--auth-ink)" opacity="0.4" />
    <line x1="330" y1="232" x2="330" y2="204" stroke="var(--auth-ink)" strokeWidth="6" strokeLinecap="round" />
    <line x1="330" y1="232" x2="356" y2="217" stroke="var(--auth-ink)" strokeWidth="5" strokeLinecap="round" />
    <circle cx="330" cy="232" r="5" fill="var(--auth-ink)" />
    {/* approved badge */}
    <circle cx="70" cy="64" r="18" fill="var(--primary-color)" />
    <path d="M62 64l6 6 11-12" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
    {/* accents */}
    <circle cx="44" cy="252" r="4" fill="var(--primary-color)" opacity="0.5" />
    <circle cx="398" cy="140" r="5" fill="var(--auth-ink)" opacity="0.25" />
    <path d="M388 62h16M396 54v16" stroke="var(--primary-color)" strokeWidth="3" strokeLinecap="round" opacity="0.6" />
  </svg>
);

// Password input with a show/hide toggle, styled for the dark auth panel.
export const AuthPasswordInput = ({
  id,
  name,
  label,
  placeholder,
  autoComplete,
  onChange,
}) => {
  const [show, setShow] = useState(false);

  return (
    <div className="auth-password">
      <label className="visually-hidden" htmlFor={id}>
        {label}
      </label>
      <input
        onChange={onChange}
        type={show ? "text" : "password"}
        name={name}
        id={id}
        className="auth-input"
        placeholder={placeholder}
        autoComplete={autoComplete}
      />
      <button
        type="button"
        className="auth-eye"
        onClick={() => setShow((s) => !s)}
        aria-label={show ? "Hide password" : "Show password"}
      >
        {show ? (
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
            <path d="M14.12 14.12A3 3 0 1 1 9.88 9.88" />
            <line x1="1" y1="1" x2="23" y2="23" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        )}
      </button>
    </div>
  );
};

// Shared split-screen shell for the auth pages (Login, ForgetPassword,
// ChangePassword). Children render inside the dark form panel, below the
// WebTime wordmark.
const AuthLayout = ({ children }) => (
  <div className="auth-page">
    <div className="auth-showcase">
      <div className="auth-showcase-inner">
        <h1>Every hour, accounted for.</h1>
        <p>
          WebTime brings crew time tracking, timesheet review, and
          payroll-ready approvals into one place.
        </p>
        <TimesheetIllustration />
      </div>
    </div>
    <div className="auth-panel">
      <div className="auth-panel-inner">
        <div className="auth-brand">
          <span className="auth-brand-mark" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3.5 2" />
            </svg>
          </span>
          <span className="auth-brand-name">WebTime</span>
        </div>
        {children}
      </div>
    </div>
  </div>
);

export default AuthLayout;
